import { ReactElement, Fragment, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import PersonSearch from 'components/UI/Input/PersonSearch/PersonSearch';
import axiosInstance from 'services/axios/axios';
import { deepCloneObject } from 'store/utility';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';
import classes from 'components/Navigation/AppBar/ImpersonationIcon/ImpersonationIcon.module.scss';
import CustomHelperText from '../base/CustomHelperText';

/**
 * Allow user to search for and select sponsor
 * @param {any} props component properties
 * @returns {ReactElement} Person-Search and display for sponsor selection
 */
const SponsorSelect = props => {
	const { setFieldTouched, setFieldValue } = useFormikContext();
	const [, meta] = useField(props);
	const [sErrorMessage, setErrorMessage] = useState('');
	const [sIsSearching, setIsSearching] = useState(false);
	const [sRows, setRows] = useState([]);
	const [sIsLoading, setIsLoading] = useState(false);
	const [sIsDialogOpen, setIsDialogOpen] = useState(!meta?.value);

	/**
	 * Helper method filtering rows stored in local state against user input. Adds a property to row objects
	 * used to determine visibility of row.
	 * @param {Event} e event object raised on input change.
	 * @returns void
	 */
	const indexRows = e => {
		let rowsCopy = deepCloneObject(sRows);
		rowsCopy.forEach(row => {
			const indexed = Object.keys(row.Cells).some(
				key =>
					row.Cells[key].Value &&
					row.Cells[key].Value.toLowerCase().includes(e.target.value)
			);
			row.isDisplayed = indexed;
		});
		setRows(rowsCopy);
	};

	/**
	 * Asynchronous handler raised on user action. Sets session variables according to selected user information.
	 * @param {Object} packet object representing clicked row object.
	 * @returns void
	 */
	const onRowClickedHandler = async packet => {
		setFieldTouched(props.name, true);
		setFieldValue(props.name, { ...packet?.payload?.Cells });
		setIsDialogOpen(false);
	};

	return (
		<Fragment>
			<Card
				variant='outlined'
				sx={{
					width: 400,
					minHeight: 100,
				}}
			>
				{meta.value ? (
					<CardContent>
						<Typography
							gutterBottom
							sx={{ color: 'text.secondary', fontSize: 14 }}
						>
							Selected Sponsor
						</Typography>
						<Typography variant='h5' component='div'>
							{meta.value.Name.Value}
						</Typography>
						<Typography sx={{ mb: 1.5 }} color='text.secondary'>
							{meta.value.Email.Value}
						</Typography>
						<Typography variant='body2'>
							Network ID: {meta.value.Network_ID.Value}
						</Typography>
						<Typography variant='body2'>
							Job Title: {meta.value.Job_Title.Value}
						</Typography>
						<Typography variant='body2'>
							Department: {meta.value.Department.Value}
						</Typography>
						<Typography variant='body2'>
							Manager: {meta.value.isManager.Value}
						</Typography>
						<CardActions>
							<Button
								size='small'
								onClick={() => {
									setIsDialogOpen(true);
								}}
							>
								Change Sponsor
							</Button>
						</CardActions>
					</CardContent>
				) : (
					<CardContent>
						<Typography
							gutterBottom
							sx={{ color: 'text.secondary', fontSize: 14 }}
						>
							No Sponsor selected.
						</Typography>
						<CardActions>
							<Button
								size='small'
								onClick={() => {
									setIsDialogOpen(true);
								}}
							>
								Select Sponsor
							</Button>
						</CardActions>
					</CardContent>
				)}
			</Card>
			<CustomHelperText error={meta.error} touched={meta.touched} />
			<Dialog
				open={sIsDialogOpen}
				onClose={() => {
					setIsLoading(false);
					setIsDialogOpen(false);
					setFieldTouched(props.name, true);
				}}
			>
				<DialogTitle>Search For Sponsor</DialogTitle>

				<WithLoadingHandler
					isLoading={sIsLoading}
					showChildren={!sIsLoading}
				>
					<DialogContent className={classes['dialog__content']}>
						<PersonSearch
							className={classes['person-search']}
							options={{ noChips: true }}
							dataTable={{
								onTextFieldChangedHandler: indexRows,
								onRowClickedHandler: onRowClickedHandler,
								rows: sRows.filter(
									row => row?.isDisplayed !== false
								),
							}}
							errorMessage={sErrorMessage}
							isLoading={sIsSearching}
							multiPerson={false}
							onButtonClickedHandler={payload => {
								setIsSearching(true);

								axiosInstance
									.post(
										'api/Components/PersonSearch',
										payload
									)
									.then(response => {
										switch (response.status) {
											case 200:
												switch (
													response.data.responseCode
												) {
													case 200:
														setRows(
															response.data
																.payload
														);
														setErrorMessage('');
														break;
													default:
														setErrorMessage(
															response.data
																.messages[0]
																.Value
														);
												}
												break;
											default:
												break;
										}
									})
									.catch(error => {
										setErrorMessage(
											error &&
												error.response &&
												error.response.data
												? error.response.data.payload
												: error.message
										);
									})
									.finally(() => {
										setIsSearching(false);
									});
							}}
							requestTarget='REQUESTEE'
							withoutFormik={true}
						/>
					</DialogContent>
				</WithLoadingHandler>
				<DialogActions>
					<Button
						autoFocus
						onClick={() => setIsDialogOpen(false)}
						variant='text'
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Fragment>
	);
};

export default SponsorSelect;
